import React, { useEffect, useState } from 'react';
import { Button, Card, Col, DatePicker, Form, Layout, message, Modal, Popconfirm, Row, Select, Space, Table, Typography } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/DataContext';
import dayjs from 'dayjs';

const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

const generateId = () => Math.random().toString(36).substring(2, 10);

const MasterclassSettings = () => {
    const { currentEventId } = useAuth();
    const [sessions, setSessions] = useState([]);
    const [venues, setVenues] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingSession, setEditingSession] = useState(null);
    const [form] = Form.useForm();
    
    const selectedVenueForm = Form.useWatch('venue', form);
    const selectedDateForm = Form.useWatch('date', form);
    
    // Derive available timeslots
    const availableTimeOptions = React.useMemo(() => {
        if (!selectedVenueForm || !selectedDateForm) return [];
        const venueData = venues.find(v => v.id === selectedVenueForm);
        if (!venueData || !venueData.sessions) return [];
        const dateStr = selectedDateForm.format('YYYY-MM-DD');
        return venueData.sessions[dateStr] || [];
    }, [selectedVenueForm, selectedDateForm, venues]);

    useEffect(() => {
        if (currentEventId) {
            fetchEventData();
        }
    }, [currentEventId]);

    const fetchEventData = async () => {
        setLoading(true);
        try {
            const docRef = doc(db, 'events', currentEventId);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                const data = docSnap.data();
                setSessions(data.masterclassSessions || []);
                setVenues(data.venues || []);
            } else {
                message.error('Event not found');
            }
        } catch (error) {
            console.error('Error fetching event data:', error);
            message.error('Failed to fetch event data');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (values) => {
        try {
            setLoading(true);
            const docRef = doc(db, 'events', currentEventId);
            const newSession = {
                id: editingSession ? editingSession.id : generateId(),
                venue: values.venue,
                date: values.date.format('YYYY-MM-DD'),
                time: values.time,
            };

            let updatedSessions;
            if (editingSession) {
                updatedSessions = sessions.map(s => s.id === editingSession.id ? newSession : s);
            } else {
                updatedSessions = [...sessions, newSession];
            }

            await updateDoc(docRef, { masterclassSessions: updatedSessions });

            message.success('Masterclass session saved successfully');
            setSessions(updatedSessions);
            setIsModalVisible(false);
        } catch (error) {
            console.error('Error saving masterclass session:', error);
            message.error('Failed to save session');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            setLoading(true);
            const docRef = doc(db, 'events', currentEventId);
            const updatedSessions = sessions.filter(s => s.id !== id);
            await updateDoc(docRef, { masterclassSessions: updatedSessions });
            message.success('Session deleted successfully');
            setSessions(updatedSessions);
        } catch (error) {
            console.error('Error deleting session:', error);
            message.error('Failed to delete session');
        } finally {
            setLoading(false);
        }
    };

    const openModal = (session = null) => {
        setEditingSession(session);
        if (session) {
            form.setFieldsValue({
                ...session,
                date: dayjs(session.date),
            });
        } else {
            form.resetFields();
        }
        setIsModalVisible(true);
    };

    const columns = [
        {
            title: 'Venue',
            dataIndex: 'venue',
            key: 'venue',
            render: (venueId) => {
                const v = venues.find(v => v.id === venueId);
                return v ? v.label : venueId;
            }
        },
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
        },
        {
            title: 'Time',
            dataIndex: 'time',
            key: 'time',
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Space>
                    <Button size="small" icon={<EditOutlined />} onClick={() => openModal(record)} />
                    <Popconfirm title="Are you sure?" onConfirm={() => handleDelete(record.id)}>
                        <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            )
        },
    ];

    return (
        <Content style={{ padding: '24px 50px', minHeight: 280 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <Title level={2}>Masterclass Sessions</Title>
                    <Text type="secondary">Manage standalone public masterclass sessions.</Text>
                </div>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>
                    Add Session
                </Button>
            </div>

            <Card style={{ marginTop: '24px' }}>
                <Table
                    columns={columns}
                    dataSource={sessions}
                    rowKey="id"
                    loading={loading}
                    pagination={false}
                />
            </Card>

            <Modal
                title={editingSession ? 'Edit Masterclass Session' : 'Add Masterclass Session'}
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                footer={null}
            >
                <Form layout="vertical" form={form} onFinish={handleSave}>
                    <Form.Item name="venue" label="Venue" rules={[{ required: true }]}>
                        <Select placeholder="Select a venue" disabled={!!editingSession}>
                            {venues.map(v => (
                                <Option key={v.id} value={v.id}>{v.label}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="date" label="Date" rules={[{ required: true }]}>
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="time" label="Time" rules={[{ required: true }]}>
                                <Select placeholder="Select a timeslot" disabled={!selectedVenueForm || !selectedDateForm}>
                                    {availableTimeOptions.map(time => (
                                        <Option key={time} value={time}>{time}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item style={{ textAlign: 'right', marginTop: '24px' }}>
                        <Button onClick={() => setIsModalVisible(false)} style={{ marginRight: 8 }}>Cancel</Button>
                        <Button type="primary" htmlType="submit" loading={loading}>Save</Button>
                    </Form.Item>
                </Form>
            </Modal>
        </Content>
    );
};

export default MasterclassSettings;
